import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import { buyerApi } from '../Buyer/BuyerService';
import type {
  AlertToggleResult,
  BuyerAlertRow,
  CreateBuyerAlertArgs,
  CreatedAlert,
  UpdateBuyerAlertArgs
} from './alertTypes';

/**
 * The buyer's saved-search alerts, `/api/buyer/alerts/*`.
 *
 * Envelope variance this slice has to absorb: the list answers `{ data: { items } }`, create
 * answers 201 `{ data: { id } }`, toggle answers `{ data: { isActive } }`, and update/delete
 * answer `{ success, message }` with NO `data` key at all. Every normalizer is module-scope,
 * takes `unknown` and is TOTAL — it never throws and never returns `undefined`.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const envelope = (response: unknown): Record<string, unknown> => asRecord(asRecord(response).data);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

/** An id the UI hands back to the API is never invented — absent means `null`. */
const asId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

export const normalizeAlertList = (response: unknown): BuyerAlertRow[] => {
  const data = asRecord(response).data;
  const items = Array.isArray(data) ? data : asRecord(data).items;
  return Array.isArray(items) ? items.map(asRecord) : [];
};

export function normalizeCreatedAlert(response: unknown): CreatedAlert {
  return { id: asId(envelope(response).id) };
}

export function normalizeToggle(response: unknown): AlertToggleResult {
  return { isActive: envelope(response).isActive === true };
}

/** `okMessage` answers `{ success, message }` with no `data`, so only the message is available. */
export function normalizeMessage(response: unknown, fallback = ''): string {
  return asString(asRecord(response).message, fallback);
}

/**
 * The dashboard's active-alert counter lives on `buyerApi` (it is a `/buyer/dashboard/*` route),
 * so every alert write has to reach across slices. Cross-slice invalidation belongs in
 * `onQueryStarted` and nowhere else. A failed write changed nothing, so it refreshes nothing.
 */
const ALERTS_PREVIEW_TAG = [{ type: 'Alert' as const, id: 'PREVIEW' }];

export const alertApi = createApi({
  reducerPath: 'alertApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['Alert'],
  endpoints: (builder) => ({
    listBuyerAlerts: builder.query<BuyerAlertRow[], void>({
      query: () => ({ url: 'buyer/alerts' }),
      transformResponse: (response: unknown) => normalizeAlertList(response),
      providesTags: [{ type: 'Alert', id: 'LIST' }]
    }),
    /** The body is rebuilt key by key, exactly as the axios service did — no passthrough. */
    createBuyerAlert: builder.mutation<CreatedAlert, CreateBuyerAlertArgs>({
      query: (body) => ({
        url: 'buyer/alerts',
        method: 'POST',
        body: {
          name: body.name,
          landTypes: body.landTypes,
          districts: body.districts,
          maxPrice: body.maxPrice,
          minArea: body.minArea,
          areaUnit: body.areaUnit
        }
      }),
      transformResponse: (response: unknown) => normalizeCreatedAlert(response),
      invalidatesTags: [{ type: 'Alert', id: 'LIST' }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(buyerApi.util.invalidateTags(ALERTS_PREVIEW_TAG));
        } catch {
          /* nothing changed server-side */
        }
      }
    }),
    updateBuyerAlert: builder.mutation<string, UpdateBuyerAlertArgs>({
      query: ({ id, body }) => ({ url: `buyer/alerts/${id}`, method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Alert', id: String(arg.id) },
        { type: 'Alert', id: 'LIST' }
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(buyerApi.util.invalidateTags(ALERTS_PREVIEW_TAG));
        } catch {
          /* nothing changed server-side */
        }
      }
    }),
    /** No body — the route flips the row and answers the new state. */
    toggleBuyerAlert: builder.mutation<AlertToggleResult, { id: number }>({
      query: ({ id }) => ({ url: `buyer/alerts/${id}/toggle`, method: 'PUT' }),
      transformResponse: (response: unknown) => normalizeToggle(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Alert', id: String(arg.id) },
        { type: 'Alert', id: 'LIST' }
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(buyerApi.util.invalidateTags(ALERTS_PREVIEW_TAG));
        } catch {
          /* nothing changed server-side */
        }
      }
    }),
    deleteBuyerAlert: builder.mutation<string, { id: number }>({
      query: ({ id }) => ({ url: `buyer/alerts/${id}`, method: 'DELETE' }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Alert', id: String(arg.id) },
        { type: 'Alert', id: 'LIST' }
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(buyerApi.util.invalidateTags(ALERTS_PREVIEW_TAG));
        } catch {
          /* nothing changed server-side */
        }
      }
    })
  })
});

export const {
  useListBuyerAlertsQuery,
  useCreateBuyerAlertMutation,
  useUpdateBuyerAlertMutation,
  useToggleBuyerAlertMutation,
  useDeleteBuyerAlertMutation
} = alertApi;
